import * as THREE from "three";
import ExtrudedLoopSubGeometryBase from "./ExtrudedLoopSubGeometryBase";
import ExtrudedLoopAnnotationData from "../sceneObjectData/ExtrudedLoopAnnotationData";
import { SceneObjectStyle } from "../sceneObject/SceneObjectStyle";

import { LineSegments2 } from "three/addons/lines/LineSegments2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";

import { createHitLine } from "../../utils/annotationUtils";
import { sceneUnitsFromMm } from "../../utils/scaler";
import Constants from "../../constants";

// 3D representation of an annotation extruded from a closed loop (Polygon/Rectangle/Circle): a
// wireframe edge outline (bottom/top/vertical segments) with an invisible wider hit-line, at or
// below the default fixed-annotation depth — replaced entirely by a solid fill mesh (sides + top
// + bottom, MeshStandardMaterial so scene lighting shades each face) once depth exceeds the
// default (the solid mesh's own faces become the click target then, so the hit-line is hidden
// along with the edges). See ExtrudedLoopSubGeometry2d for the 2D (flat top-down projection,
// depth-independent) sibling.
export default abstract class ExtrudedLoopSubGeometry3d<T extends ExtrudedLoopAnnotationData> extends ExtrudedLoopSubGeometryBase<T> {

    // Fixed regardless of SceneObjectStyle (unlike the edge/text materials). DoubleSide because
    // Area/Polygon profiles are user-drawn and not guaranteed to wind consistently. Slightly
    // transparent so the solid doesn't fully obscure geometry behind/inside it (e.g. other
    // annotations, or the surface it's placed on).
    private static readonly solidMaterial = new THREE.MeshStandardMaterial({
        color: 0x8A97A6,
        roughness: 0.8,
        metalness: 0.05,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.75
    });

    private edgesGeometry: LineGeometry;
    private readonly edges: LineSegments2;
    private readonly edgesHitLine: LineSegments2; // Invisible, wider hit target kept in sync with edgesGeometry
    private edgesMaterial: LineMaterial;

    private readonly solidMesh: THREE.Mesh; // Full solid fill (sides+top+bottom), shown only once depth exceeds the default, replacing the wireframe

    // Leaf classes construct their own initial (Normal-style) materials from their own static
    // caches, so each shape type keeps independent GPU resources, then pass them here.
    protected constructor(initialLineMaterial: LineMaterial, initialTextMaterial: THREE.MeshBasicMaterial) {
        super(initialTextMaterial);

        this.edgesMaterial = initialLineMaterial;
        this.edgesGeometry = new LineGeometry();
        this.edges = new LineSegments2(this.edgesGeometry, this.edgesMaterial);
        this.edgesHitLine = createHitLine(this.edgesGeometry);

        this.solidMesh = new THREE.Mesh(new THREE.BufferGeometry(), ExtrudedLoopSubGeometry3d.solidMaterial);
        this.solidMesh.visible = false;

        this.solidGroup.add(this.solidMesh);
        this.solidGroup.add(this.edges);
        this.solidGroup.add(this.edgesHitLine);
        this.solidGroup.add(this.textGroup);
    }

    // Leaf classes resolve a style to their own cached line material (see e.g. AreaAnnotationGeometry3d).
    protected abstract getLineMaterialForStyle(style: SceneObjectStyle): LineMaterial;

    protected updateGeometry(data: T, forceUpdate: boolean = false): boolean {

        const profile = this.computeProfile(data);
        if (!profile) {
            this.edges.visible = false;
            this.edgesHitLine.visible = false;
            this.textGroup.visible = false;
            this.solidMesh.visible = false;
            return false;
        }
        const { points2d, depth, isOpenPreview, centroid } = profile; // depth here is |data.depth| — used only for the solid-visibility threshold below

        this.solidGroup.position.copy(data.placement.location);
        this.solidGroup.quaternion.copy(data.placement.getRotationQuaternion());
        // Geometry below is built directly from the signed data.depth (not this magnitude), so a
        // negative depth extrudes to the opposite side on its own — no group rotation needed.
        // Rotating solidGroup 180° about X to "flip" direction (as this used to do) also flips the
        // profile's own Y axis as an unwanted side effect, mirroring the visible shape.

        if (isOpenPreview) {
            this.updateOpenPreviewLine(points2d);
            this.edges.visible = true;
            this.edgesHitLine.visible = true;
            this.textGroup.visible = false;
            this.solidMesh.visible = false;
        } else {
            this.updateEdgesGeometry(points2d, data.depth);
            // Offset the label a little further past the top face, away from the profile —
            // "further" means more negative Z when depth itself is negative.
            const labelZ = data.depth + Math.sign(data.depth || 1) * ExtrudedLoopSubGeometryBase.labelZOffset;
            this.updateAreaLabel(data.computeEnclosedArea(), data.computeEnclosedVolume(), data.showAreaText, data.showVolume, centroid, labelZ, data.placement.axis, data.fontSize);

            // Past the default depth, a solid fill reads far better than an overlapping wireframe
            // cage — replace the wireframe entirely rather than layering the fill underneath it.
            const showSolid = depth > sceneUnitsFromMm(Constants.DEFAULT_EXTRUSION_DEPTH_MM);
            if (showSolid) this.updateSolidMesh(points2d, data.depth);
            this.solidMesh.visible = showSolid;
            this.edges.visible = !showSolid;
            this.edgesHitLine.visible = !showSolid;
        }

        this.data = data.clone();
        return true;
    }

    private updateOpenPreviewLine(points2d: THREE.Vector2[]): void {

        const segments: number[] = [];
        for (let i = 0; i < points2d.length - 1; i++) {
            const p = points2d[i];
            const next = points2d[i + 1];
            segments.push(p.x, p.y, 0, next.x, next.y, 0);
        }

        this.edgesGeometry.dispose();
        this.edgesGeometry = new LineGeometry();
        this.edgesGeometry.setPositions(segments);
        this.edges.geometry = this.edgesGeometry;
        this.edgesHitLine.geometry = this.edgesGeometry;
        this.edges.computeLineDistances();
    }

    private updateEdgesGeometry(points2d: THREE.Vector2[], depth: number): void {

        const segments: number[] = [];
        const pushSegment = (ax: number, ay: number, az: number, bx: number, by: number, bz: number) => {
            segments.push(ax, ay, az, bx, by, bz);
        };

        for (let i = 0; i < points2d.length; i++) {
            const p = points2d[i];
            const next = points2d[(i + 1) % points2d.length];

            pushSegment(p.x, p.y, 0, next.x, next.y, 0); // Bottom edge
            pushSegment(p.x, p.y, depth, next.x, next.y, depth); // Top edge
            pushSegment(p.x, p.y, 0, p.x, p.y, depth); // Vertical edge
        }

        this.edgesGeometry.dispose();
        this.edgesGeometry = new LineGeometry();
        this.edgesGeometry.setPositions(segments);
        this.edges.geometry = this.edgesGeometry;
        this.edgesHitLine.geometry = this.edgesGeometry;
        this.edges.computeLineDistances();
    }

    // Full closed solid (sides + top + bottom caps) extruded from the profile. Built via
    // THREE.ExtrudeGeometry rather than hand-rolled triangulation: it triangulates the cap faces
    // (via earcut internally) correctly even for non-convex profiles, which Area/Polygon
    // annotations can be (user-drawn), and extrudes from z=0 to z=depth — matching this class's
    // own convention exactly, including sign: a negative depth extrudes toward -Z without any
    // extra handling here (ExtrudeGeometry accepts either sign natively).
    private updateSolidMesh(points2d: THREE.Vector2[], depth: number): void {

        const shape = new THREE.Shape(points2d);
        const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
        geometry.computeVertexNormals(); // Ensure correct per-face shading normals for MeshStandardMaterial lighting

        this.solidMesh.geometry.dispose();
        this.solidMesh.geometry = geometry;
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;

        const lineMat = this.getLineMaterialForStyle(style);
        this.edges.material = lineMat;
        this.edgesMaterial = lineMat;

        super.setStyle(style);
    }

    dispose(): void {
        this.edgesGeometry.dispose();
        this.solidMesh.geometry.dispose();
        super.dispose();
    }
}
